import { formatAmountWithUnit, sellBackCents } from "@loot-os/api-contract";
import type { InventoryItem } from "@loot-os/client";
import { Alert } from "@loot-os/design-system/core/Alert";
import { ConfirmDialog } from "@loot-os/design-system/core/ConfirmDialog";
import { Amount } from "@loot-os/design-system/domain/Amount";
import { ItemArt } from "@loot-os/design-system/domain/ItemArt";
import { useState } from "react";

import { useCurrency } from "../lib/currency.js";
import { saleErrorText, useSellInventoryItems } from "../lib/inventory.js";
import { useTerminology } from "../lib/terminology.js";

/**
 * Confirms selling units back (boxed's "Confirm Sale"), on the design system's ConfirmDialog:
 * what goes, what it credits, and that it cannot be undone. The sale is all or nothing, so a
 * refusal names the unit that stopped it and the dialog stays, with the selection left alone for
 * the shopper to fix.
 */
export function SellBackDialog({
  open,
  onOpenChange,
  items,
  onSold,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: InventoryItem[];
  /** Called once the sale committed, so the page can drop the selection it just sold. */
  onSold: () => void;
}) {
  const currency = useCurrency();
  const terms = useTerminology();
  const sell = useSellInventoryItems();
  const [error, setError] = useState<string | null>(null);
  const totalCents = sellBackCents(items);

  const confirm = async () => {
    setError(null);
    try {
      await sell.mutateAsync(items.map((item) => item.id));
      onSold();
    } catch (err) {
      setError(saleErrorText(err, terms));
      // Rejecting keeps the dialog open, with the reason in it.
      throw err;
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Sell back to the house"
      description={`${items.length} ${items.length === 1 ? terms.item.singular : terms.item.plural} for their value. This can't be undone, and a sold ${terms.item.singular} can no longer be shipped to you.`}
      confirmLabel={`Sell back · ${formatAmountWithUnit(totalCents, currency)}`}
      onConfirm={confirm}
    >
      <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3">
            <span className="size-9 shrink-0 overflow-hidden rounded-md bg-bg-raised">
              <ItemArt src={item.item.imageUrl} alt="" shape="square" className="size-full" />
            </span>
            <span className="min-w-0 flex-1 truncate font-body text-body-b2 text-text-primary">
              {item.item.name}
            </span>
            <Amount cents={item.item.value} size="meta" className="text-text-secondary" />
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between border-t border-border-subtle pt-3">
        <span className="font-body text-body-b2 text-text-secondary">You receive</span>
        <Amount cents={totalCents} className="text-text-primary" />
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
    </ConfirmDialog>
  );
}
